// src/components/atoms/landing/cta-button.tsx
import Link from "next/link";
import { ArrowRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { isAbsoluteUrl, isNonRouterHref } from "@/lib/links";
import type { CtaPlacement } from "@/lib/posthog/link-events";

type Variant = "primary" | "outline" | "ghost" | "ghost-brand";
type Size = "md" | "lg";

type CommonProps = {
  href: string;
  variant?: Variant;
  size?: Size;
  trailingIcon?: React.ReactNode | false;
  className?: string;
  external?: boolean;
};

/**
 * `ctaPlacement` is read by the landing page's delegated click listener
 * (`landing-link-analytics.tsx`) — this component stays a server component
 * and never fires the capture itself. When it is set, `children` must be a
 * plain string: it is rendered into `data-cta-label` so the click listener
 * reads the source string rather than the (possibly machine-translated)
 * visible text.
 */
type CtaButtonProps = CommonProps &
  (
    | { ctaPlacement: CtaPlacement; children: string }
    | { ctaPlacement?: undefined; children: React.ReactNode }
  );

const base =
  "inline-flex shrink-0 items-center justify-center gap-2 rounded-lg font-display font-medium tracking-[0.02em] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-white focus-visible:ring-brand disabled:pointer-events-none disabled:opacity-50 min-h-11";

const variantClass: Record<Variant, string> = {
  primary: "bg-brand text-white hover:bg-brand/90",
  outline:
    "border-2 border-brand bg-transparent text-brand hover:bg-brand hover:text-white",
  ghost: "text-brand-ink hover:bg-brand-muted",
  "ghost-brand": "text-brand hover:bg-brand-muted",
};

const sizeClass: Record<Size, string> = {
  md: "h-12 px-8 text-base",
  lg: "h-16 px-12 text-lg",
};

export function CtaButton({
  href,
  children,
  variant = "primary",
  size = "lg",
  trailingIcon,
  className,
  external = false,
  ctaPlacement,
}: CtaButtonProps) {
  const icon =
    trailingIcon === false
      ? null
      : trailingIcon ?? <ArrowRight aria-hidden className="size-4" />;

  const classes = cn(base, variantClass[variant], sizeClass[size], className);

  // Proxied (/docs) or absolute hrefs aren't Next routes — use a plain <a> so
  // next/link doesn't fire an RSC prefetch that 404s. Open a new tab only for
  // truly external URLs (or when the caller asks).
  const newTab = external || isAbsoluteUrl(href);
  if (external || isNonRouterHref(href)) {
    return (
      <a
        href={href}
        className={classes}
        data-cta-placement={ctaPlacement}
        data-cta-label={ctaPlacement ? children : undefined}
        {...(newTab ? { target: "_blank", rel: "noopener noreferrer" } : {})}
      >
        {children}
        {icon}
      </a>
    );
  }

  return (
    <Link
      href={href}
      className={classes}
      data-cta-placement={ctaPlacement}
      data-cta-label={ctaPlacement ? children : undefined}
    >
      {children}
      {icon}
    </Link>
  );
}
